import PubSub from "pubsub-js"

class ContentManager {
  constructor(options = {}) {
    this.editorRef = null
    this.currentContent = ""
    this.isProcessingContentUpdateRef = { current: false }
    this.onUpdate = null
    this.generateUpdatedHTML = null
    this.extractStyleContent = null

    // Bind methods
    this.initialize = this.initialize.bind(this)
    this.updateContent = this.updateContent.bind(this)
    this.handleContentUpdate = this.handleContentUpdate.bind(this)
    this.handleDataOnlyUpdate = this.handleDataOnlyUpdate.bind(this)
    this.extractBodyContent = this.extractBodyContent.bind(this)
    this.prepareContentForSave = this.prepareContentForSave.bind(this)
    this.normalizeParagraphs = this.normalizeParagraphs.bind(this)
    this.processCDNAttributes = this.processCDNAttributes.bind(this)
    this.convertLinksToButtons = this.convertLinksToButtons.bind(this)
    this.injectStylesFromHTML = this.injectStylesFromHTML.bind(this)
    this.applySurgicalUpdates = this.applySurgicalUpdates.bind(this)

    this.subscriptions = []
  }

  initialize({ editorRef, content, onUpdate, generateUpdatedHTML, extractStyleContent, url, processCDNAttributes, styleManager }) {
    this.editorRef = editorRef
    this.onUpdate = onUpdate
    this.generateUpdatedHTML = generateUpdatedHTML
    this.extractStyleContent = extractStyleContent
    this.url = url
    this.processCDNAttributesUtil = processCDNAttributes
    this.styleManager = styleManager

    // Initialize content
    if (content && content !== this.currentContent) {
      this.updateContent(content)
    }

    // Set up PubSub subscriptions
    this.setupSubscriptions()
  }

  setupSubscriptions() {
    // Subscribe to content updates from StyleClassEditor
    const contentUpdateToken = PubSub.subscribe(
      "html-editor:content-update",
      this.handleContentUpdate
    )

    // Subscribe to requests for current content
    const getCurrentContentToken = PubSub.subscribe(
      "html-editor:get-current-content",
      this.handleGetCurrentContent.bind(this)
    )

    // Subscribe to data-only updates from ClassPropertyDrawer
    const dataOnlyToken = PubSub.subscribe(
      "html-editor:data-only-update",
      this.handleDataOnlyUpdate
    )

    this.subscriptions.push(contentUpdateToken, getCurrentContentToken, dataOnlyToken)
  }

  updateContent(content) {
    if (this.editorRef?.current && content && content !== this.currentContent) {
      this.currentContent = content

      const bodyContent = this.extractBodyContent(content)

      this.editorRef.current.innerHTML = this.normalizeParagraphs(bodyContent)
      this.processCDNAttributes(this.editorRef.current)
      this.convertLinksToButtons(this.editorRef.current)

      // Extract and inject styles from the HTML head into the document head
      if (content.includes("<style")) {
        this.injectStylesFromHTML(content)
      }
    }
  }

  handleContentUpdate(msg, data) {
    // Process all CSS surgical updates - no source filtering needed with centralized system
      // Set flag to prevent horizontal menu from appearing during update
      this.isProcessingContentUpdateRef.current = true

      // Handle updates based on type
      if (data.updates && Array.isArray(data.updates)) {
        // Use surgical updates for specific changes
        const updatedDocument = this.applySurgicalUpdates(data.updates)

        // CRITICAL FIX: Store the updated document back to currentContent
        if (updatedDocument) {
          this.currentContent = updatedDocument

          // IMMEDIATE VISUAL UPDATE: Inject updated styles immediately
          this.injectStylesFromHTML(updatedDocument)
        }

        // Clear the flag and trigger onUpdate after a delay
        setTimeout(() => {
          this.isProcessingContentUpdateRef.current = false
          if (this.onUpdate && typeof this.onUpdate === "function") {
            this.onUpdate(updatedDocument || this.currentContent)
          }
        }, 150)

        return // Exit early for surgical updates
      }

      // Full document update path
      this.currentContent = data.htmlContent

      // Update the editor content
      if (this.editorRef?.current) {
        const bodyContent = this.extractBodyContent(data.htmlContent)
        const cleanedBodyContent = this.cleanEditorContent(bodyContent)
        this.editorRef.current.innerHTML = cleanedBodyContent
        this.processCDNAttributes(this.editorRef.current)
        this.convertLinksToButtons(this.editorRef.current)
      }

      // Trigger onUpdate callback if provided and content actually changed
      if (this.onUpdate && typeof this.onUpdate === "function" && data.htmlContent !== this.currentContent) {
        this.onUpdate(data.htmlContent)
      }

      // Clear the flag
      this.isProcessingContentUpdateRef.current = false
  }

  handleDataOnlyUpdate(msg, data) {
    if (data.htmlContent) {
      // Update the data source without any DOM manipulation
      this.currentContent = data.htmlContent

      // Trigger onUpdate callback to persist changes
      if (this.onUpdate && typeof this.onUpdate === "function") {
        this.onUpdate(data.htmlContent)
      }
    }
  }

  handleGetCurrentContent(msg, data) {
    // Get the current complete document and respond
    const currentContent = this.getCompleteDocument()

    PubSub.publish("html-editor:current-content-response", {
      requestId: data.requestId,
      htmlContent: currentContent
    })
  }

  extractBodyContent(htmlContent) {
    if (!htmlContent) return "<p><br></p>"

    // Always inject styles from the HTML content
    this.injectStylesFromHTML(htmlContent)

    // If it's a full HTML document, extract body content
    if (htmlContent.includes("<!DOCTYPE") || htmlContent.includes("<html")) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlContent, "text/html")
      return this.normalizeParagraphs(doc.body.innerHTML)
    }

    return htmlContent
  }

  injectStylesFromHTML(htmlContent) {
    if (!htmlContent) return

    // Use StyleManager for proper style injection and scoping
    if (this.styleManager) {
      this.styleManager.injectStylesFromHTML(htmlContent, ".editor")
    } else {
      // Fallback to the old method if StyleManager is not available
      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlContent, "text/html")

      // Extract and inject styles into the editor
      const styleElements = doc.querySelectorAll("style")

      if (styleElements.length > 0) {
        // Remove existing injected styles
        const existingStyles = document.querySelectorAll("style[data-html-editor]")
        existingStyles.forEach((style) => style.remove())

        // Inject new styles with scoping to the editor
        styleElements.forEach((styleEl) => {
          const newStyle = document.createElement("style")
          newStyle.setAttribute("data-html-editor", "true")

          // Scope styles to the editor container with basic logic
          const scopedCSS = styleEl.textContent.replace(
            /([^{}]+)\s*\{/g,
            (match, selector) => {
              const trimmedSelector = selector.trim()

              // Don't scope body or html selectors, just remove them
              if (trimmedSelector === "body" || trimmedSelector === "html") {
                return ".editor {"
              }
              // Scope other selectors to the editor
              return `.editor ${trimmedSelector} {`
            }
          )
          newStyle.textContent = scopedCSS
          document.head.appendChild(newStyle)
        })
      }
    }
  }

  normalizeParagraphs(htmlContent) {
    return htmlContent
  }

  processCDNAttributes(container) {
    if (!container) return

    // Use the passed processCDNAttributes utility if available
    if (this.processCDNAttributesUtil) {
      this.processCDNAttributesUtil(container)
      return
    }

    // Fallback implementation
    const elementsWithCDN = container.querySelectorAll("[cdn]")
    elementsWithCDN.forEach((element) => {
      const cdnValue = element.getAttribute("cdn")
      if (cdnValue) {
        const fullUrl = this.url ? this.url(cdnValue) : cdnValue
        if (element.tagName.toLowerCase() === "img") {
          element.setAttribute("src", fullUrl)
        } else if (element.tagName.toLowerCase() === "a") {
          element.setAttribute("href", fullUrl)
        } else if (
          element.tagName.toLowerCase() === "video" ||
          element.tagName.toLowerCase() === "audio"
        ) {
          element.setAttribute("src", fullUrl)
        }
      }
    })
  }

  convertLinksToButtons(container) {
    if (!container) return

    const links = container.querySelectorAll("a")
    links.forEach((link) => {
      // Skip if already has editor-button class
      if (link.classList.contains("editor-button")) return

      // Only add editor-button class for functionality
      link.classList.add("editor-button")

      // Make non-editable to prevent cursor issues
      link.contentEditable = "false"
    })
  }

  prepareContentForSave(html) {
    if (!html) return ""

    // Process CDN attributes using string replacement to preserve HTML formatting
    let processedHtml = html

    // Pattern to match elements with cdn attribute and remove their src/href
    processedHtml = processedHtml.replace(
      /<(img|video|audio)\s+([^>]*?)cdn\s*=\s*"[^"]*"([^>]*?)src\s*=\s*"[^"]*"([^>]*?)>/gi,
      '<$1 $2cdn="$3"$4>'
    )

    processedHtml = processedHtml.replace(
      /<(img|video|audio)\s+([^>]*?)src\s*=\s*"[^"]*"([^>]*?)cdn\s*=\s*"[^"]*"([^>]*?)>/gi,
      '<$1 $2$3cdn="$4">'
    )

    // Handle anchor tags with cdn and href
    processedHtml = processedHtml.replace(
      /<a\s+([^>]*?)cdn\s*=\s*"[^"]*"([^>]*?)href\s*=\s*"[^"]*"([^>]*?)>/gi,
      '<a $1cdn="$2"$3>'
    )

    processedHtml = processedHtml.replace(
      /<a\s+([^>]*?)href\s*=\s*"[^"]*"([^>]*?)cdn\s*=\s*"[^"]*"([^>]*?)>/gi,
      '<a $1$2cdn="$3">'
    )

    return processedHtml
  }

  cleanEditorContent(content) {
    if (!content) return ""

    const parser = new DOMParser()
    const doc = parser.parseFromString(content, "text/html")

    // Remove editor UI elements that should not be saved
    const uiElements = doc.querySelectorAll(
      '.ae-button-transform-gizmo, .ae-image-transform-gizmo, .ae-transform-gizmo, [class*="transform-gizmo"], [class*="editor-ui"], [data-editor-ui]'
    )
    uiElements.forEach((element) => element.remove())

    // Get the cleaned content
    let cleanedContent = doc.body.innerHTML

    // Remove excessive empty lines
    cleanedContent = cleanedContent
      .replace(/\n{3,}/g, '\n\n')
      .replace(/(<br\s*\/?>[\s\n]*){3,}/gi, '<br><br>')
      .replace(/(\n[\s]*<br\s*\/?>[\s\n]*){2,}/gi, '\n<br>')
      .replace(/[ \t]+$/gm, '')

    return cleanedContent
  }

  applySurgicalUpdates(updates) {
    // Use currentContent (HTML editor content) instead of window document
    let updatedDocument = this.currentContent

    if (!updatedDocument) {
      return null
    }

    updates.forEach((update) => {
      const { type, selector, content, property, value, oldSelector, newSelector } = update

      switch (type) {
        case "css-class":
          updatedDocument = this.applyCSSClassUpdate(selector, content, updatedDocument)
          break

        case "css-property":
          updatedDocument = this.applyCSSPropertyUpdate(selector, property, value, updatedDocument)
          break

        case "css-class-rename":
          updatedDocument = this.applyCSSClassRename(oldSelector, newSelector, content, updatedDocument)
          break

        case "css-class-delete":
          updatedDocument = this.applyCSSClassDelete(selector, updatedDocument)
          break

        case "html-element":
          updatedDocument = this.applyHTMLElementUpdate(selector, content, updatedDocument)
          break

        default:
          console.warn("Unknown update type:", type)
      }
    })

    return updatedDocument
  }

  applyCSSClassUpdate(className, newCSSContent, documentToUpdate = null) {

    let currentDocument = documentToUpdate || this.currentContent

    if (!currentDocument) return currentDocument

    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(currentDocument, "text/html")

      // Find or create style elements
      let styleElement = doc.querySelector('style')
      if (!styleElement) {
        styleElement = doc.createElement('style')
        doc.head.appendChild(styleElement)
      }

      let cssContent = styleElement.textContent || ""

      // Remove existing class definition
      const classRegex = new RegExp(`\\.${className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\{[^}]*\\}`, 'g')
      cssContent = cssContent.replace(classRegex, '')

      // Add new class definition
      if (newCSSContent && newCSSContent.trim()) {
        const formattedCSS = `.${className} {\n${newCSSContent}\n}`
        cssContent += '\n' + formattedCSS
      }

      // Clean up extra whitespace
      cssContent = cssContent.replace(/\n\s*\n/g, '\n').trim()
      styleElement.textContent = cssContent

      currentDocument = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML
    } catch (error) {
      console.error("Error applying CSS class update:", error)
    }

    return currentDocument
  }

  applyCSSPropertyUpdate(selector, property, value, documentToUpdate = null) {
    let currentDocument = documentToUpdate || this.currentContent

    if (!currentDocument) {
      return currentDocument
    }

    if (selector.startsWith(".")) {
      const className = selector.substring(1)

      // Parse the existing CSS to get a reliable structure
      const cssInfo = this.parseCSSFromDocument(currentDocument)

      if (cssInfo.styleContent) {
        // Parse existing CSS rules into a lookup table
        const cssRules = this.parseCSSRules(cssInfo.styleContent)

        // Update or add the property
        if (!cssRules[className]) {
          cssRules[className] = {}
        }
        cssRules[className][property] = value

        // Regenerate CSS from the lookup table
        const newStyleContent = this.generateCSSFromRules(cssRules)

        // Replace the style content in the document
        currentDocument = currentDocument.replace(cssInfo.fullStyleTag, `<style>${newStyleContent}</style>`)
      } else {
        // No style tag exists, create one
        const newStyleTag = `<style>\n.${className} {\n  ${property}: ${value};\n}\n</style>`
        const headMatch = currentDocument.match(/<\/head>/i)
        if (headMatch) {
          currentDocument = currentDocument.replace(/<\/head>/i, `${newStyleTag}\n</head>`)
        } else {
          // No head tag, add to beginning of document
          currentDocument = newStyleTag + '\n' + currentDocument
        }
      }
    }

    return currentDocument
  }

  // Parse CSS from document and return style tag info
  parseCSSFromDocument(document) {
    const styleTagRegex = /<style[^>]*>([^]*?)<\/style>/i
    const match = styleTagRegex.exec(document)

    if (match) {
      return {
        fullStyleTag: match[0],
        styleContent: match[1],
        hasStyleTag: true
      }
    }

    return {
      fullStyleTag: null,
      styleContent: null,
      hasStyleTag: false
    }
  }

  // Parse CSS content into a lookup table of rules
  parseCSSRules(cssContent) {
    const rules = {}

    // Match CSS class rules including pseudo-classes: .className:hover { property: value; }
    const classRegex = /\.([a-zA-Z][\w-]*(?::[a-zA-Z-]+(?:\([^)]*\))?)*)\s*\{([^}]*)\}/g
    let match

    while ((match = classRegex.exec(cssContent)) !== null) {
      const className = match[1]
      const properties = match[2]

      if (!rules[className]) {
        rules[className] = {}
      }

      // Parse individual properties within the class
      const propertyRegex = /([a-zA-Z-]+)\s*:\s*([^;]+);?/g
      let propMatch

      while ((propMatch = propertyRegex.exec(properties)) !== null) {
        const propName = propMatch[1].trim()
        const propValue = propMatch[2].trim()
        rules[className][propName] = propValue
      }
    }

    return rules
  }

  // Generate CSS content from rules lookup table
  generateCSSFromRules(rules) {
    let css = ''

    for (const [className, properties] of Object.entries(rules)) {
      if (Object.keys(properties).length > 0) {
        css += `.${className} {\n`

        for (const [property, value] of Object.entries(properties)) {
          if (value && value.toString().trim()) {
            css += `  ${property}: ${value};\n`
          }
        }

        css += '}\n\n'
      }
    }

    return css.trim()
  }

  applyHTMLElementUpdate(selector, content, documentToUpdate = null) {
    let currentDocument = documentToUpdate || this.currentContent

    if (currentDocument && selector !== "body") {
      const parser = new DOMParser()
      const doc = parser.parseFromString(currentDocument, "text/html")
      const targetElement = doc.querySelector(selector)

      if (targetElement) {
        targetElement.innerHTML = content
        currentDocument = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML
      }
    }

    return currentDocument
  }

  applyCSSClassRename(oldClassName, newClassName, content, documentToUpdate = null) {
    let currentDocument = documentToUpdate || this.currentContent

    if (!currentDocument) return currentDocument

    // First delete the old class
    currentDocument = this.applyCSSClassDelete(oldClassName, currentDocument)

    // Then create the new class
    currentDocument = this.applyCSSClassUpdate(newClassName, content, currentDocument)

    return currentDocument
  }

  applyCSSClassDelete(className, documentToUpdate = null) {
    let currentDocument = documentToUpdate || this.currentContent

    if (!currentDocument) return currentDocument

    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(currentDocument, "text/html")

      // Remove the CSS class definition from style elements
      const styleElements = doc.querySelectorAll('style')

      styleElements.forEach((styleEl) => {
        let cssContent = styleEl.textContent

        // Remove the specific class and its content
        const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const classRegex = new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*\\}`, 'g')
        cssContent = cssContent.replace(classRegex, '')

        // Clean up any empty lines
        cssContent = cssContent.replace(/\n\s*\n/g, '\n').trim()
        styleEl.textContent = cssContent
      })

      currentDocument = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML
    } catch (error) {
      console.error("Error deleting CSS class:", error)
    }

    return currentDocument
  }

  getCurrentContent() {
    return this.currentContent
  }

  getCompleteDocument() {
    if (this.editorRef?.current) {
      const editorHTML = this.editorRef.current.innerHTML || ""

      // If we have editor content but no currentContent (e.g., fresh paste), build a minimal document
      if (editorHTML && !this.currentContent) {
        const cleanedEditorContent = this.cleanEditorContent(editorHTML)
        return `<!DOCTYPE html>
<html>
<head>
<style>
</style>
</head>
<body>
${cleanedEditorContent}
</body>
</html>`
      }

      // If we have currentContent, preserve its head and update body
      if (this.currentContent) {
        // Parse the current full document to preserve head content
        const parser = new DOMParser()
        const doc = parser.parseFromString(this.currentContent, "text/html")

        // Update the body with the current editor content
        const cleanedEditorContent = this.cleanEditorContent(editorHTML)

        doc.body.innerHTML = cleanedEditorContent

        // Return the complete HTML document
        return "<!DOCTYPE html>\n" + doc.documentElement.outerHTML
      }
    }

    return this.currentContent
  }

  destroy() {
    // Clean up subscriptions
    this.subscriptions.forEach((token) => {
      PubSub.unsubscribe(token)
    })

    // Clean up injected styles when component unmounts
    const injectedStyles = document.querySelectorAll("style[data-html-editor]")
    injectedStyles.forEach((style) => style.remove())

    this.subscriptions = []
  }
}

// Export as a plugin-compatible object
export default {
  create: (options) => new ContentManager(options),
  ContentManager,
}